import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const formatDate = (s) => s ? new Date(s).toLocaleString('en-ZA') : '—';
const isOutOfCredit = (reason) => (reason || '').toLowerCase().includes('insufficient funds');

const ShipmentsPage = () => {
  const [failures, setFailures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retrying, setRetrying] = useState(null);
  const [results, setResults] = useState({});
  const [pendingRelease, setPendingRelease] = useState([]);
  const [releaseRetrying, setReleaseRetrying] = useState(null);
  const [releaseResults, setReleaseResults] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [res, pending] = await Promise.all([
        api.get('/admin/orders/shipment-failures'),
        api.get('/admin/orders/tradesafe-release-pending')
      ]);
      setFailures(res.data || []);
      setPendingRelease(pending.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load shipment failures.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const retry = async (orderNumber) => {
    setRetrying(orderNumber);
    try {
      // Booking calls the courier, which can take longer than the default timeout
      const res = await api.post(`/admin/orders/${orderNumber}/retry-shipment`, {}, { timeout: 60000 });
      setResults(r => ({ ...r, [orderNumber]: res.data }));
      await load();
    } catch (e) {
      setResults(r => ({ ...r, [orderNumber]: { booked: false, shipmentFailureReason: e.response?.data?.error || e.message } }));
    } finally {
      setRetrying(null);
    }
  };

  const retryRelease = async (orderNumber) => {
    setReleaseRetrying(orderNumber);
    try {
      const res = await api.post(`/admin/orders/${orderNumber}/retry-accept-delivery`, {}, { timeout: 60000 });
      setReleaseResults(r => ({ ...r, [orderNumber]: res.data }));
      await load();
    } catch (e) {
      setReleaseResults(r => ({ ...r, [orderNumber]: { accepted: false, error: e.response?.data?.error || e.message } }));
    } finally {
      setReleaseRetrying(null);
    }
  };

  const anyOutOfCredit = failures.some(f => isOutOfCredit(f.shipmentFailureReason));

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h2 style={{ margin: 0 }}>Shipments</h2>
          <p style={{ margin: '4px 0 0', color: '#7f8c8d', fontSize: '13px' }}>
            Paid orders whose Pudo waybill could not be booked. The seller can't ship until it is.
            The server retries automatically (4 attempts); Retry here starts again immediately.
          </p>
        </div>
        <button onClick={load} style={{ padding: '8px 16px', cursor: 'pointer' }}>Refresh</button>
      </div>

      {anyOutOfCredit && (
        <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '14px 18px', marginBottom: '16px', color: '#856404', fontSize: '14px' }}>
          <strong>The Courier Guy account is out of credit.</strong> Waybills are paid from Eduthrift's prepaid
          Courier Guy account. Top it up in the Courier Guy portal, then press Retry below.
        </div>
      )}

      {error && <p style={{ color: 'red' }}>{error}</p>}

      {Object.entries(results).map(([orderNumber, r]) => (
        <div key={orderNumber} style={{
          backgroundColor: r.booked ? '#e8f5e9' : '#fdecea', border: `1px solid ${r.booked ? '#27ae60' : '#e74c3c'}`,
          borderRadius: '6px', padding: '10px 14px', marginBottom: '10px', fontSize: '13px'
        }}>
          {r.booked
            ? <>✅ <strong>{orderNumber}</strong> booked — waybill <strong>{r.trackingNumber}</strong>. The seller has been emailed drop-off instructions.</>
            : <>❌ <strong>{orderNumber}</strong> still failing: {r.shipmentFailureReason}</>}
        </div>
      ))}

      {loading ? (
        <p>Loading...</p>
      ) : failures.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#666', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
          <p style={{ fontSize: '18px' }}>No failed waybill bookings</p>
          <p style={{ fontSize: '14px' }}>Every paid order has been booked with the courier.</p>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th style={th}>Order</th>
              <th style={th}>Seller / Buyer</th>
              <th style={th}>Locker</th>
              <th style={th}>Attempts</th>
              <th style={th}>Reason</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {failures.map(f => (
              <tr key={f.orderNumber} style={{ borderBottom: '1px solid #eee' }}>
                <td style={td}><strong>{f.orderNumber}</strong><br /><span style={muted}>{f.itemName}</span></td>
                <td style={td}>{f.sellerEmail}<br /><span style={muted}>→ {f.buyerEmail}</span></td>
                <td style={td}>{f.deliveryLockerId || '—'}<br /><span style={muted}>{f.serviceLevelCode}</span></td>
                <td style={td}>{f.shipmentAttempts}<br /><span style={muted}>last {formatDate(f.lastShipmentAttemptAt)}</span></td>
                <td style={{ ...td, maxWidth: '360px', wordBreak: 'break-word', color: '#c0392b' }}>
                  {isOutOfCredit(f.shipmentFailureReason) && <strong>Courier Guy account out of credit. </strong>}
                  {f.shipmentFailureReason}
                </td>
                <td style={td}>
                  <button
                    onClick={() => retry(f.orderNumber)}
                    disabled={!!retrying}
                    style={{ ...btn, backgroundColor: '#3498db', color: '#fff' }}
                  >
                    {retrying === f.orderNumber ? 'Booking…' : 'Retry'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ marginTop: '40px', marginBottom: '16px' }}>
        <h2 style={{ margin: 0 }}>Awaiting TradeSafe release</h2>
        <p style={{ margin: '4px 0 0', color: '#7f8c8d', fontSize: '13px' }}>
          The courier confirmed these orders were delivered, but TradeSafe hasn't paid the seller yet.
          Retry tells TradeSafe again that delivery was accepted. The order leaves this list once
          TradeSafe confirms the payout, which can take a few minutes.
        </p>
      </div>

      {Object.entries(releaseResults).map(([orderNumber, r]) => (
        <div key={orderNumber} style={{
          backgroundColor: r.accepted ? '#e8f5e9' : '#fdecea', border: `1px solid ${r.accepted ? '#27ae60' : '#e74c3c'}`,
          borderRadius: '6px', padding: '10px 14px', marginBottom: '10px', fontSize: '13px'
        }}>
          {r.accepted
            ? <>✅ <strong>{orderNumber}</strong> accepted by TradeSafe{r.state ? <> (allocation {r.state})</> : null}. Waiting for TradeSafe to confirm the payout.</>
            : <>❌ <strong>{orderNumber}</strong> TradeSafe still rejects it: {r.error}</>}
        </div>
      ))}

      {!loading && (pendingRelease.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
          <p style={{ fontSize: '16px', margin: 0 }}>No delivered orders waiting on TradeSafe</p>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th style={th}>Order</th>
              <th style={th}>Seller / Buyer</th>
              <th style={th}>Waybill</th>
              <th style={th}>Delivered</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {pendingRelease.map(o => (
              <tr key={o.orderNumber} style={{ borderBottom: '1px solid #eee' }}>
                <td style={td}><strong>{o.orderNumber}</strong><br /><span style={muted}>{o.itemName}</span></td>
                <td style={td}>{o.sellerEmail}<br /><span style={muted}>→ {o.buyerEmail}</span></td>
                <td style={td}>{o.trackingNumber || '—'}<br /><span style={muted}>TradeSafe {o.tradeSafeTransactionId}</span></td>
                <td style={td}>{formatDate(o.deliveryConfirmedDate)}</td>
                <td style={td}>
                  <button
                    onClick={() => retryRelease(o.orderNumber)}
                    disabled={!!releaseRetrying}
                    style={{ ...btn, backgroundColor: '#3498db', color: '#fff' }}
                  >
                    {releaseRetrying === o.orderNumber ? 'Sending…' : 'Retry'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ))}
    </div>
  );
};

const th = { padding: '10px 12px', textAlign: 'left', fontWeight: '600', borderBottom: '2px solid #ddd' };
const td = { padding: '10px 12px', verticalAlign: 'top' };
const muted = { color: '#888', fontSize: '12px' };
const btn = { padding: '6px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' };

export default ShipmentsPage;
